import { all, call, fork, put, select, takeEvery } from "redux-saga/effects"
import {
  getInboxChatUsersFailed,
  getInboxChatUsersSuccess,
  getInboxMessagesFailed,
  getInboxMessagesSuccess,
  getMessageLikesFailed,
  getMessageLikesSuccess,
  incrementUnreadMessageCount,
  initInboxSessionFailed,
  initInboxSessionSuccess,
  loadInboxMessagesFailed,
  loadInboxMessagesSuccess,
  resetUnreadMessageCounter,
  saveCommentBoardDialogs,
  saveDirectMessageDialogs,
  saveDirectMessageUsers,
  saveNewsFeedDialogs,
  sendInboxMessageFailed,
  updateMessageLikesFailed,
  updateMessageLikesSuccess,
  sendPmChatNotification as sendPmChatNotificationAction,
  sendInboxMessageSuccess,
  saveNewInboxMessage,
} from "./actions"
import { showToast } from "utils"
import {
  connectToChat,
  connectToDialog,
  createSession,
  getDialogs,
  getMessages,
  getPrivateDialogMessages,
  getUnreadMessageCount,
  joinGroupDialog,
  sendMessageWithAttachment,
  sendTextMessage,
  subscribeToChat,
} from "services/quickblox"
import { get, post } from "helpers/api_helper"
import { LOCAL_STORAGE_TAGS } from "constants/index"
import {
  GET_INBOX_CHAT_USERS,
  GET_INBOX_MESSAGES,
  GET_MESSAGE_LIKES,
  INIT_INBOX_SESSION,
  LOAD_INBOX_MESSAGES,
  SEND_INBOX_MESSAGE,
  SEND_PM_CHAT_NOTIFICATION,
  UPDATE_MESSAGE_LIKES,
} from "./actionTypes"
import { GET_EXPERIENCES_URL } from "helpers/url_helper"
import store from "../index"
import { sortDialogsByDate } from "containers/Inbox/utils"
import { sendChatNotification } from "store/actions"

const fetchUsersByEventId = async eventId => {
  return await get(`event/${eventId}/users`)
}

function* fetchUsersByEventIds(eventIds) {
  try {
    const usersByEventId = yield all(
      eventIds.map(eventId =>
        call(function* () {
          try {
            const users = yield call(fetchUsersByEventId, eventId)
            return { eventId, users }
          } catch (error) {
            console.error(
              `Failed to fetch users for event ID ${eventId}:`,
              error
            )
            return { eventId, users: null }
          }
        })
      )
    )
    const result = usersByEventId.reduce((acc, { eventId, users }) => {
      acc[eventId] = users
      return acc
    }, {})
    return result
  } catch (error) {
    console.error("Error fetching users by event IDs:", error)
  }
}

function* fetchAndStoreUsersByEventIds(eventIds, cbDialogs) {
  try {
    const usersByEventId = yield call(fetchUsersByEventIds, eventIds)
    const updatedDialogItems = cbDialogs?.items?.map(dialog => {
      const eventId = dialog.data?.event_id
      return {
        ...dialog,
        userList: eventId
          ? usersByEventId[eventId] || []
          : dialog.userList || [],
      }
    })

    const updatedDialogs = {
      ...cbDialogs,
      items: updatedDialogItems.sort(sortDialogsByDate),
    }

    yield put(saveCommentBoardDialogs(updatedDialogs))
  } catch (error) {
    console.error("Error in fetchAndAttachUsers:", error)
  }
}

function* getCommentBoardDialogs() {
  try {
    const commentBoards = yield get("user/comment-boards")
    if (!commentBoards || commentBoards.length === 0) {
      return null
    }
    const commentBoardDialogIds = commentBoards.map(cb => cb.dialog_id)
    const commentBoardDialogs = yield getDialogs({
      _id: { in: commentBoardDialogIds },
      sort_desc: "last_message_date_sent",
    })

    const eventIds = commentBoards?.reduce((acc, item) => {
      if (!!item?.event_id && !acc.includes(item?.event_id)) {
        acc.push(item?.event_id)
      }
      return acc
    }, [])

    const dialogItems = commentBoardDialogs?.items?.map(dialog => {
      const commentBoard = commentBoards?.find(
        cb => cb?.dialog_id === dialog?._id
      )
      if (commentBoard) {
        return {
          ...dialog,
          data: {
            event_id: commentBoard?.event_id,
            menu_item_id: commentBoard?.menu_item_id,
          },
          experience_name: commentBoard?.name,
          menu_item_name: commentBoard?.event_name,
          title: commentBoard?.event_name || dialog?.name,
          subTitle: commentBoard?.name,
          feature_type: 1,
          userList: [],
        }
      } else
        return {
          ...dialog,
          feature_type: 1,
        }
    })
    const dialogsWithData = {
      ...commentBoardDialogs,
      items: dialogItems.sort(sortDialogsByDate),
    }

    yield put(saveCommentBoardDialogs(dialogsWithData))

    return { commentBoardDialogs: dialogsWithData, eventIds }
  } catch (error) {
    console.error("Comment Board Dialog Fetch Error", error)
    throw error
  }
}

const getDialogDisplayDetails = (
  dialog,
  experience,
  userId,
  userMap,
  menu_item
) => {
  let title = experience?.name || dialog?.name
  let subTitle = !menu_item ? null : menu_item?.label
  if (
    !experience &&
    (dialog?.data?.event_id === -1 || !dialog?.data) &&
    dialog?.occupants_ids?.filter(x => x !== userId)?.length === 1
  ) {
    const userDetails =
      userMap[dialog?.occupants_ids?.filter(x => x !== userId)[0]]
    title =
      (userDetails?.first_name ? userDetails?.first_name + " " : "---") +
      (userDetails?.last_name ? userDetails.last_name : "---")
    subTitle = "Direct Message"
    return { title, subTitle }
  }
  if (
    !experience &&
    dialog?.data?.event_id === -1 &&
    dialog?.occupants_ids?.filter(x => x !== userId)?.length > 1
  ) {
    const userDetails = userMap[dialog?.user_id]
    title =
      (userDetails?.first_name ? userDetails?.first_name + " " : "---") +
      (userDetails?.last_name ? userDetails.last_name : "---")
    subTitle = "Direct Message"
    return { title, subTitle }
  }

  return { title, subTitle }
}

function* getDirectMessageDialogs(currentUserId) {
  try {
    const directMessageDialogs = yield getDialogs({
      type: { in: [2, 3] },
      sort_desc: "last_message_date_sent",
    })
    let dialogsWithData = directMessageDialogs
    if (
      directMessageDialogs &&
      directMessageDialogs.items &&
      directMessageDialogs.items.length > 0
    ) {
      const events = yield get(GET_EXPERIENCES_URL)

      const menu_item_ids = directMessageDialogs?.items.reduce((acc, item) => {
        if (
          item?.data?.menu_item_id !== undefined &&
          !acc.includes(item?.data?.menu_item_id)
        ) {
          acc.push(item?.data?.menu_item_id)
        }
        return acc
      }, [])

      let menuItemLabels = []
      if (!!menu_item_ids && menu_item_ids?.length > 0) {
        menuItemLabels = yield post("/menu-items-labels", { menu_item_ids })
      }

      const occupants_ids = [
        ...new Set(
          directMessageDialogs?.items.flatMap(dialog => dialog.occupants_ids)
        ),
      ]

      const directMessageUsers = yield post("/get-users", {
        qb_ids: occupants_ids,
      })

      const userMap = {}
      directMessageUsers?.data?.forEach(user => {
        if (user?.chat_credentials?.qb_id)
          userMap[user?.chat_credentials?.qb_id] = user
      })
      yield put(saveDirectMessageUsers(userMap))

      const dialogItems = directMessageDialogs?.items?.map(item => {
        const experience =
          !item?.data?.event_id || item?.data?.event_id === -1
            ? null
            : events?.find(e => e?.id === item?.data?.event_id)
        const menu_item = !item?.data?.menu_item_id
          ? null
          : menuItemLabels?.find(
            menu => menu?.menu_item_id === item?.data?.menu_item_id
          )
        const { title, subTitle } = getDialogDisplayDetails(
          item,
          experience,
          currentUserId,
          userMap,
          menu_item
        )
        return {
          ...item,
          experience_name: !experience ? null : experience?.name,
          menu_item_name: !menu_item ? null : menu_item?.label,
          title,
          subTitle,
          feature_type: 2,
          userList: item?.occupants_ids?.map(id => userMap[id]),
        }
      })

      dialogsWithData = {
        ...directMessageDialogs,
        items: dialogItems.sort(sortDialogsByDate),
        unreadCount: directMessageDialogs?.items?.reduce(
          (acc, current) => acc + current?.unread_messages_count,
          0
        ),
      }
    }
    yield put(saveDirectMessageDialogs(dialogsWithData))
    return dialogsWithData
  } catch (error) {
    console.error("Direct Message Dialog Fetch Error")
    throw error
  }
}

function* getNewsFeedDialogs() {
  try {
    const newsFeed = yield get("/comment-board/menu-item")
    if (!newsFeed) {
      return null
    }
    const newsFeedDialogs = yield getDialogs({
      _id: { in: newsFeed.dialog_id },
      sort_desc: "last_message_date_sent",
    })

    const dialogItems = newsFeedDialogs?.items?.map(item => ({
      ...item,
      feature_type: 3,
      title: item?.name || "News Feed",
    }))

    const dialogsWithData = {
      ...newsFeedDialogs,
      items: dialogItems.sort(sortDialogsByDate),
    }
    yield put(saveNewsFeedDialogs(dialogsWithData))
    return dialogsWithData
  } catch (error) {
    console.error("News Feed Dialog Fetch Error")
    throw error
  }
}

async function onSystemListener(
  message,
  userMap,
  currentUserId,
  directMessageDialogs,
  commentBoardDialogs,
  dispatch
) {
  if (message.extension && message.extension.notification_type === "1") {
    if (message.extension.dialog_id) {
      const dialog = await connectToDialog(message.extension.dialog_id)
      if (dialog?.type === 2 || dialog?.type === 3) {
        // Check for duplicates early
        const isDialogAlreadyPresent = directMessageDialogs?.items?.some(
          item => item._id === dialog._id
        )
        if (isDialogAlreadyPresent) {
          return false // Skip processing to avoid duplicate dialog & message
        }

        if (dialog?.type !== 3) {
          await joinGroupDialog(dialog?._id)
        }

        let exp = null
        let menu_item = null

        if (dialog?.data?.event_id && dialog?.data?.event_id !== -1) {
          exp = await get(`/admin/event/${dialog?.data?.event_id}`)
          if (dialog?.data?.menu_item_id) {
            const data = await post("/menu-items-labels", {
              menu_item_ids: [dialog?.data?.menu_item_id],
            })
            if (data?.length > 0) {
              menu_item = data[0]
            }
          }
        }

        let newUserIds = []
        dialog?.occupants_ids?.forEach(id => {
          if (!userMap[id]) {
            newUserIds.push(id)
          }
        })

        let updatedUserMap = { ...userMap }
        if (newUserIds?.length > 0) {
          const users = await post("/get-users", {
            qb_ids: newUserIds,
          })
          users?.data?.forEach(user => {
            if (user?.chat_credentials?.qb_id)
              updatedUserMap[user?.chat_credentials?.qb_id] = user
          })
        }

        const { title, subTitle } = getDialogDisplayDetails(
          dialog,
          exp,
          currentUserId,
          updatedUserMap,
          menu_item
        )

        const updatedDialog = {
          ...dialog,
          experience_name: !exp ? null : exp?.name,
          menu_item_name: !menu_item ? null : menu_item?.label,
          title,
          subTitle,
          feature_type: 2,
          userList: dialog?.occupants_ids?.map(id => updatedUserMap[id]),
        }

        dispatch(
          saveDirectMessageDialogs({
            ...directMessageDialogs,
            items: [updatedDialog, ...directMessageDialogs?.items],
            unreadCount:
              directMessageDialogs?.unreadCount +
              updatedDialog?.unread_messages_count,
          })
        )
      } else {
        const commentBoards = await get("user/comment-boards")
        if (!commentBoards || commentBoards.length === 0) {
          return false
        }

        let updatedDialog = { ...dialog }
        const cb = commentBoards.find(x => x?.dialog_id === dialog?._id)
        if (!!cb && cb?.event_id) {
          const users = await fetchUsersByEventId(cb?.event_id)
          updatedDialog = {
            ...updatedDialog,
            data: {
              event_id: cb?.event_id,
              menu_item_id: cb?.menu_item_id,
            },
            experience_name: cb?.name,
            menu_item_name: cb?.event_name,
            title: cb?.event_name || dialog?.name,
            subTitle: cb?.name,
            feature_type: 1,
            userList: users,
          }
        } else {
          updatedDialog = {
            ...updatedDialog,
            feature_type: 1,
          }
        }

        dispatch(
          saveCommentBoardDialogs({
            ...commentBoardDialogs,
            items: [updatedDialog, ...commentBoardDialogs?.items],
          })
        )
      }
    }
    return false
  }
}

function* initInboxSession({
  payload: { login, password, newsFeed_enabled, onMessageListener },
}) {
  try {
    const inboxSession = yield createSession(login, password)
    yield connectToChat(inboxSession.user_id, inboxSession?.token)
    const [cbDialogs, directMessageDialogs, newsFeedDialogs] = yield all([
      call(getCommentBoardDialogs),
      call(getDirectMessageDialogs, inboxSession.user_id),
      call(getNewsFeedDialogs),
    ])

    subscribeToChat(
      (userId, message) => {
        const state = store.getState()
        const { directMessageDialogs, commentBoardDialogs, newsFeedDialogs } =
          state.Inbox
        onMessageListener(userId, message, {
          directMessageDialogs,
          commentBoardDialogs,
          newsFeedDialogs,
        })
      },
      message => {
        const state = store.getState()
        const {
          directMessageDialogs,
          directMessageUserMap,
          commentBoardDialogs,
        } = state.Inbox
        const dispatch = store.dispatch
        onSystemListener(
          message,
          directMessageUserMap,
          inboxSession.user_id,
          directMessageDialogs,
          commentBoardDialogs,
          dispatch
        )
      }
    )
    const { commentBoardDialogs, eventIds } = cbDialogs ?? {}
    yield put(initInboxSessionSuccess(inboxSession))
    if (
      directMessageDialogs &&
      directMessageDialogs.items &&
      directMessageDialogs.items.length > 0
    ) {
      yield all(
        directMessageDialogs.items.map(dialog =>
          fork(function* () {
            try {
              if (dialog.type !== 3) {
                yield call(joinGroupDialog, dialog.xmpp_room_jid)
              }
            } catch (error) {
              console.error(
                `Failed to join group for ${dialog.xmpp_room_jid}:`,
                error
              )
            }
          })
        )
      )
    }
    if (
      commentBoardDialogs &&
      commentBoardDialogs.items &&
      commentBoardDialogs.items.length > 0
    ) {
      yield all(
        commentBoardDialogs.items.map(dialog =>
          fork(function* () {
            try {
              yield call(joinGroupDialog, dialog.xmpp_room_jid)
            } catch (error) {
              console.error(
                `Failed to join group for ${dialog.xmpp_room_jid}:`,
                error
              )
            }
          })
        )
      )
      yield fork(fetchAndStoreUsersByEventIds, eventIds, commentBoardDialogs)
    }
  } catch (error) {
    yield put(initInboxSessionFailed(error))
    showToast("error", "Inbox connection failed")
  }
}

function* getInboxMessages({ payload: params }) {
  try {
    let messages = null
    if (params?.dialog?.type === 3) {
      messages = yield getPrivateDialogMessages(
        params?.dialog,
        params?.beforeDateSent
      )
    } else {
      messages = yield getMessages(params?.dialog, params?.beforeDateSent)
    }
    yield put(getInboxMessagesSuccess(messages))
    yield put(resetUnreadMessageCounter({ dialogId: params?.dialog?._id }))
  } catch (error) {
    yield put(getInboxMessagesFailed())
    showToast("error", "Loading messages error", "Error")
  }
}

function* loadInboxMessages({ payload: params }) {
  try {
    let messages = null
    if (params?.dialog?.type === 3) {
      messages = yield getPrivateDialogMessages(
        params?.dialog,
        params?.beforeDateSent
      )
    } else {
      messages = yield getMessages(params?.dialog, params?.beforeDateSent)
    }
    yield put(loadInboxMessagesSuccess(messages))
  } catch (error) {
    yield put(loadInboxMessagesFailed(error))
    showToast("error", "Loading messages error", "Error")
  }
}

function* getChatUsers({ payload: params }) {
  try {
    let users = [...params?.currentUserList]
    const userIds = new Set(users.map(user => user?.chat_credentials?.qb_id))
    const newUserIds = [
      ...new Set(
        params?.messages
          .map(msg => msg.sender_id)
          .filter(sender_id => !userIds.has(sender_id))
      ),
    ]
    let newUsers = []
    if (newUserIds?.length > 0) {
      newUsers = yield post("/get-users", {
        qb_ids: newUserIds,
      })
    }

    const chatUsers = [...users, ...(newUsers?.data || [])]

    yield put(getInboxChatUsersSuccess(chatUsers))
  } catch (error) {
    yield put(getInboxChatUsersFailed(error))
  }
}

function* getMessageLikes({ payload: params }) {
  try {
    const response = yield get("/qb/likes", { params })
    yield put(getMessageLikesSuccess(response))
  } catch (error) {
    yield put(getMessageLikesFailed(error))
  }
}

function* updateMessageLikes({ payload: params }) {
  try {
    const response = yield post("/qb/toggle-like", {
      dialog_qb_id: params?.dialog_id,
      message_qb_id: params?.message_qb_id,
      vendor_id: params?.vendorId,
    })
    yield put(
      updateMessageLikesSuccess({
        liked_by_me: params?.liked_by_me,
        message_qb_id: params?.message_qb_id,
        total_likes: params?.total_likes,
      })
    )
  } catch (error) {
    yield put(updateMessageLikesFailed(error))
    showToast("error", error, "Error")
  }
}

function* sendPmChatNotification({ payload: params }) {
  try {
    yield post("/chat-notifications/private-message", params)
  } catch (error) {
    console.error(error)
  }
}

function* sendInboxMessage({ payload: params }) {
  try {
    const {
      xmpp_room_jid,
      file,
      message,
      feature_type,
      event_id,
      menu_item_id,
      sender_id,
      vendor_id,
      dialog_id,
      receiver_ids,
      dialog_type,
      opponent_id,
      qbId,
    } = params
    if (file) {
      const { messageId, attachments } = yield call(
        sendMessageWithAttachment,
        xmpp_room_jid,
        file?.file,
        message,
        dialog_type,
        opponent_id,
        dialog_id
      )
      if (messageId) {
        yield put(
          saveNewInboxMessage({
            _id: messageId,
            attachments: attachments,
            date_sent: Date.now(),
            message: message,
            sender_id: qbId,
            created_at: Date.now(),
          })
        )
      }
    } else if (message?.trim()?.length) {
      const messageId = yield call(
        sendTextMessage,
        xmpp_room_jid,
        message,
        dialog_type,
        opponent_id,
        dialog_id
      )
      if (messageId) {
        yield put(
          saveNewInboxMessage({
            _id: messageId,
            attachments: [],
            date_sent: Date.now(),
            message: message,
            sender_id: qbId,
            created_at: Date.now(),
          })
        )
      }
    }
    yield put(sendInboxMessageSuccess())
    if (feature_type === 1 || feature_type === 3) {
      yield put(
        sendChatNotification({
          sender_id,
          ...(event_id && event_id !== -1 ? { event_id: event_id } : {}),
          vendor_id,
          ...(menu_item_id ? { menu_item_id: menu_item_id } : {}),
          dialog_id,
        })
      )
    }
    if (feature_type === 2) {
      yield put(
        sendPmChatNotificationAction({
          vendor_id,
          sender_id,
          receiver_ids,
          dialog_id,
          event_id: !!event_id ? event_id : -1,
        })
      )
    }
  } catch (error) {
    yield put(sendInboxMessageFailed(error))
    const errors = error?.message?.errors
    let message = "Please try again"
    if (typeof errors === "string") {
      message = errors
    } else if (typeof errors === "object") {
      message = Object.entries(errors)
        ?.map(([key, value]) => {
          const errorMessage = Array.isArray(value) ? value?.join(", ") : value
          return `${key}: ${errorMessage}`
        })
        .join("; ")
    }
    showToast("error", message, "Error")
  }
}

function* inboxSaga() {
  yield takeEvery(INIT_INBOX_SESSION, initInboxSession)
  yield takeEvery(GET_INBOX_MESSAGES, getInboxMessages)
  yield takeEvery(GET_INBOX_CHAT_USERS, getChatUsers)
  yield takeEvery(LOAD_INBOX_MESSAGES, loadInboxMessages)
  yield takeEvery(GET_MESSAGE_LIKES, getMessageLikes)
  yield takeEvery(UPDATE_MESSAGE_LIKES, updateMessageLikes)
  yield takeEvery(SEND_PM_CHAT_NOTIFICATION, sendPmChatNotification)
  yield takeEvery(SEND_INBOX_MESSAGE, sendInboxMessage)
}

export default inboxSaga
